import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ContentType } from '../../pages/content-creation/content-creation';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { PlanningType } from '../../types/planning.type';
import { VisibilityType } from '../../types/visibility.type';
import { ColorService } from '../../services/color.service';
import { InputRadioElement } from '../input-radio-element/input-radio-element';
import { InputRadioElementItem } from '../../types/input-radio-element-item.type';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-publication-block',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    UpperFirstLetterPipe,
    NgClass,
    ReactiveFormsModule,
    InputRadioElement,
    IconComponent,
  ],
  templateUrl: './publication-block.html',
  styleUrl: './publication-block.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicationBlock {
  readonly contentType: InputSignal<ContentType> = input.required<ContentType>();
  readonly publicationForm: InputSignal<FormGroup> = input.required<FormGroup>();
  readonly visibilityDisabled: InputSignal<boolean> = input<boolean>(false);

  colorService: ColorService = inject(ColorService);
  settingsService: SettingsService = inject(SettingsService);
  textColor: string = this.colorService.getCssVariable('--text-color');
  textColor500: string = this.colorService.getCssVariable('--neutral-500');

  visibilityModes: VisibilityType[] = ['public', 'private'];
  planningModes: Signal<PlanningType[]> = computed(() =>
    this.contentType() === 'workout' || this.contentType() === 'gps-only'
      ? ['immediate']
      : ['immediate', 'scheduled'],
  );
  visiblityItemList: Signal<InputRadioElementItem<VisibilityType>[]> = computed(() => [
    ...(this.contentType() !== 'gps-only'
      ? [
          {
            value: 'public' as const,
            icon: 'eye_on' as const,
            title: 'publication_block.visibility.public',
            subTitle: 'publication_block.visibility.advice.public',
            default: true as const,
          },
        ]
      : []),
    ...(this.contentType() === 'workout'
      ? [
          {
            value: 'onlyFollower' as const,
            icon: 'multiplayers' as const,
            title: 'publication_block.visibility.community',
            subTitle: 'publication_block.visibility.advice.community',
          },
        ]
      : []),
    {
      value: 'private',
      icon: 'lock_open',
      title: 'publication_block.visibility.private',
      subTitle: 'publication_block.visibility.advice.private',
    },
  ]);

  userTimezone: Signal<string | null> = computed(() => {
    return `${this.settingsService.timezone()} ${this.settingsService.timezone_offset()}`;
  });

  get visibilityControl(): FormControl {
    return this.publicationForm().get('visibility') as FormControl;
  }

  get planningControl(): FormControl {
    return this.publicationForm().get('planning') as FormControl;
  }

  get planningDateControl(): FormControl {
    return this.publicationForm().get('planningDate') as FormControl;
  }

  get planningTimeControl(): FormControl {
    return this.publicationForm().get('planningTime') as FormControl;
  }

  visibilityChange(visibilityMode: string | number | boolean) {
    if (this.publicationForm()) {
      this.publicationForm().get('visibility')?.setValue(visibilityMode.toString());
    }
  }
}
